import {COMPONENT_ID} from '@advisor/util/constants';

/**
 * freeze mapping object make the property read only
 *
 * featureEnvFlag: ['local', 'dev', 'qa'] will only
 * show the buttons in their respective environments
 * No featureEnvFlag will show in all
 *
 * FEATURE_FLAG: key from the rw-adv-options.js.
 * If set to `false` in that file will not show the button,
 * otherwise always will.
 *
 * featureEnvFlag and FEATURE_FLAG form a set intersection
 * */
export const quickBtnMapping = Object.freeze({
  openAccount: 'OPENACCOUNT',
  addClient: 'ADDCLIENT',
  addGoal: 'ADDGOAL',
  addUser: 'ADDUSER',
  widgets: 'WIDGETS',
  createSegment: 'CREATESEGMENT',
  transferMoney: 'TRANSFER_MONEY',
  personalizeDocLink: 'PERSONALIZE_DOC_LINK',
  uploadDocument: 'UPLOAD_DOCUMENT',
  addWorkflow: 'ADD_WORKFLOW',
  initiateAccountTransfer: 'INITIATE_ACCOUNT_TRANSFER',
  'add New Question': 'ADDNEWQUESTION',
  clientAuthExportAsCsv: 'CLIENT_AUTH_EXPORT_AS_CSV',
  clientAuthExportAsPdf: 'CLIENT_AUTH_EXPORT_AS_PDF',
  openClientAccount: 'OPENCLIENTACCOUNT',
});

export const aliasMapping = Object.freeze({
  openAccount: '/advisor/client/open-new-account/?',
  addClient: '/advisor/open-client-account/',
  widgets: '/widgets',
  transferMoney: '/advisor/client/initiate-transfer-money',
  personalizeDocLink: '',
  uploadDocument: '',
  addWorkflow: '/advisor/documents/workflow/add',
  initiateAccountTransfer: '/advisor/initiate-account-transfer',
  clientAuthExportAs: '',
});

export const OPEN_ACCOUNT = new Object({
  id: quickBtnMapping.openAccount,
  name: 'Open Account',
  alias: aliasMapping.openAccount,
  accessible: true,
  dialogOpen: false,
  label: 'Open Account',
});

export const ADD_CLIENT = new Object({
  id: quickBtnMapping.addClient,
  name: 'Add Client',
  alias: aliasMapping.addClient,
  accessible: true,
  dialogOpen: false,
  label: 'Add Client',
  title: 'Add a new Client',
});

export const ADD_GOAL = new Object({
  id: quickBtnMapping.addGoal,
  name: 'Add Goal',
  accessible: true,
  dialogOpen: false,
  label: 'Add Goal',
});

export const ADD_USER = new Object({
  id: quickBtnMapping.addUser,
  name: 'Add User',
  accessible: true,
  dialogOpen: false,
  dialogDelete: false,
  label: 'Add User',
});

export const TRANSFER_MONEY = new Object({
  id: quickBtnMapping.transferMoney,
  name: 'Transfer Money',
  alias: aliasMapping.transferMoney,
  accessible: true,
  dialogOpen: false,
  dialogDelete: false,
  label: 'Transfer Money',
});

export const WIDGETS = new Object({
  id: quickBtnMapping.widgets,
  name: 'Widgets',
  alias: aliasMapping.widgets,
  accessible: true,
  dialogOpen: false,
  label: 'Widgets',
});

/**
 * Keeping this for future use.
 */
export const CREATE_SEGMENT = new Object({
  id: quickBtnMapping.createSegment,
  name: 'Create New Segment',
  accessible: true,
  dialogOpen: false,
  label: 'Create a new Segment',
});

export const PERSONALIZE_DOCUMENT_LINK = new Object({
  id: quickBtnMapping.personalizeDocLink,
  name: 'Personalize your document',
  alias: aliasMapping.personalizeDocLink,
  accessible: true,
  dialogOpen: false,
  label: 'Personalize your document',
  isText: true,
  class: 'font-weight-light',
});

export const UPLOAD_DOCUMENT = new Object({
  id: quickBtnMapping.uploadDocument,
  name: 'Upload Document',
  alias: aliasMapping.uploadDocument,
  accessible: true,
  dialogOpen: false,
  label: 'Upload Document',
});

export const ADD_WORKFLOW = new Object({
  id: quickBtnMapping.addWorkflow,
  name: 'Add Workflow',
  alias: aliasMapping.addWorkflow,
  accessible: true,
  dialogOpen: false,
  label: 'Add Workflow',
});

export const ADD_NEW_QUESTION_RTQ = new Object({
  id: quickBtnMapping['add New Question'],
  name: 'Add New Question',
  accessible: true,
  dialogOpen: false,
  dialogDelete: false,
  label: 'Add New Question',
});

export const INITIATE_ACCOUNT_TRANSFER = new Object({
  id: quickBtnMapping.initiateAccountTransfer,
  name: 'Initiate Account Transfer',
  alias: aliasMapping.initiateAccountTransfer,
  accessible: true,
  dialogOpen: false,
  label: 'Initiate Account Transfer',
});

export default {
  /**
   * 1:Dashboard
   * 2:Clients
   * 10:Manage Access
   * 11:Private Label
   * 14:Activity
   * 16:Settings
   *
   * Below component IDs are not defined in the database.
   * Only use for RwIwantToDropdown mapping
   * 20: Goal
   * 21: Account
   * 22: HOUSEHOLD
   * 23: CLIENTS_HOUSEHOLD
   * 99: COMMON
   * !!important: the index number is actually the componentId, make sure it's unique.
   * Need to check the index is not occupied by any other component
   *
   * Note: in order to show a button outside of its normal component add
   * a platformArea field with the original component name.
   *
   * Alias field will redirect o that path
   * */

  [COMPONENT_ID.DASHBOARD]: [
    TRANSFER_MONEY,
    ADD_CLIENT,
    // WIDGETS
  ],
  [COMPONENT_ID.CLIENTS_HOUSEHOLD]: [ADD_CLIENT, TRANSFER_MONEY],

  [COMPONENT_ID.CLIENTS]: [ADD_GOAL, TRANSFER_MONEY, INITIATE_ACCOUNT_TRANSFER, ADD_CLIENT],
  [COMPONENT_ID.HOUSEHOLD]: [ADD_GOAL, TRANSFER_MONEY, ADD_CLIENT],

  [COMPONENT_ID.INVMODEL]: [],
  [COMPONENT_ID.SETTINGS_MANAGE_RIA]: [],

  [COMPONENT_ID.MANAGE_ACCESS]: [ADD_USER],

  [COMPONENT_ID.PRIVATE_LABEL]: [],

  [COMPONENT_ID.CLIENT_SEGMENTS]: [CREATE_SEGMENT],
  [COMPONENT_ID.GOALS]: [TRANSFER_MONEY, INITIATE_ACCOUNT_TRANSFER, ADD_CLIENT],
  [COMPONENT_ID.ACCOUNT]: [TRANSFER_MONEY, INITIATE_ACCOUNT_TRANSFER, ADD_CLIENT],
  [COMPONENT_ID.DOCUMENTS]: [PERSONALIZE_DOCUMENT_LINK, UPLOAD_DOCUMENT, ADD_WORKFLOW],
  [COMPONENT_ID.INVESTMENT_SETUP]: [],
  [COMPONENT_ID.RISK_TOLERANCE]: [ADD_NEW_QUESTION_RTQ],
  [COMPONENT_ID.ACTIVITY]: [TRANSFER_MONEY, ADD_CLIENT, INITIATE_ACCOUNT_TRANSFER],
  [COMPONENT_ID.COMMON]: [],
  [COMPONENT_ID.CLIENT_AUTHENTICATION]: [],
  [COMPONENT_ID.CLIENT_AUTH_BANK_LINKING]: [],
  [COMPONENT_ID.ADO_SUPERVISOR_APPROVAL]: [],
};
